import {type ReactNode, useCallback, useEffect, useMemo, useState} from "react";
import {useEncounter} from "@/encounter/EncounterHooks.ts";
import {useRealtimeChannel} from "@/api/realtime/RealtimeChannelHooks.ts";
import {deleteEncounterLight, deleteLightOverride, getEncounterLights, saveEncounterLight,
    saveLightOverride, saveLevelLighting as persistLevelLighting,
    resetLevelLighting as deleteLevelLighting} from "@/api/EncounterLightApi.ts";
import type {DirectionalLight} from "@/dungeons/lighting/DirectionalLight.ts";
import {EncounterLightContext} from "./EncounterLightHooks.ts";
import {type EncounterLight, type EncounterLightOverride, type EncounterLightPreview, type EncounterLights,
    withLightPreview, type LevelLightingOverride} from "./EncounterLights.ts";

const empty: EncounterLights = {overrides: [], lights: [], levelLightingOverrides: []};

export default function EncounterLightProvider({children}: {children: ReactNode}) {
    const encounter = useEncounter();
    const {subscribe} = useRealtimeChannel<EncounterLights>(`/encounters/${encounter.id}/lights`);
    const [state, setState] = useState<EncounterLights>(empty);
    const [preview, setPreview] = useState<EncounterLightPreview>();
    const [levelPreview, setLevelPreview] = useState<LevelLightingOverride>();
    const [load, setLoad] = useState<{id?: string; error: boolean}>({error: false});

    useEffect(() => {
        let active = true;
        let loaded = false;
        let buffered: EncounterLights | undefined;
        const unsubscribe = subscribe(next => {
            if (!active) return;
            if (loaded) setState(next);
            else buffered = next;
        });
        void getEncounterLights(encounter).then(snapshot => {
            if (!active) return;
            setState(buffered ?? snapshot);
            loaded = true;
            setLoad({id: encounter.id, error: false});
        }).catch(() => {if (active) setLoad({id: encounter.id, error: true});});
        return () => {active = false; unsubscribe();};
    }, [encounter, subscribe]);

    const saveOverride = useCallback(async (light: EncounterLightOverride) => {
        setState(await saveLightOverride(encounter, light));
    }, [encounter]);
    const deleteOverride = useCallback(async (id: string) => {
        setState(await deleteLightOverride(encounter, id));
    }, [encounter]);
    const saveLight = useCallback(async (light: EncounterLight) => {
        setState(await saveEncounterLight(encounter, light));
    }, [encounter]);
    const deleteLight = useCallback(async (id: string) => {
        setState(await deleteEncounterLight(encounter, id));
    }, [encounter]);
    const previewLight = useCallback((next: EncounterLightPreview) => setPreview(next), []);
    const clearPreview = useCallback(() => setPreview(undefined), []);
    const saveLevelLighting = useCallback(async (levelId: string, light: DirectionalLight) => {
        setState(await persistLevelLighting(encounter, levelId, light));
    }, [encounter]);
    const resetLevelLighting = useCallback(async (levelId: string) => {
        setState(await deleteLevelLighting(encounter, levelId));
    }, [encounter]);
    const previewLevelLighting = useCallback((levelId: string, light: DirectionalLight) =>
        setLevelPreview({levelId, light}), []);
    const clearLevelLightingPreview = useCallback(() => setLevelPreview(undefined), []);
    const visibleState = useMemo(() => {
        const next = withLightPreview(state, preview);
        return levelPreview ? {...next, levelLightingOverrides: [
            ...(next.levelLightingOverrides ?? []).filter(item => item.levelId !== levelPreview.levelId),
            levelPreview,
        ]} : next;
    }, [state, preview, levelPreview]);
    const value = useMemo(() => ({state: visibleState, persistedState: state,
        initialLoading: load.id !== encounter.id,
        saveOverride, deleteOverride, saveLight, deleteLight, previewLight, clearPreview,
        saveLevelLighting, resetLevelLighting, previewLevelLighting, clearLevelLightingPreview}),
    [visibleState, state, load, encounter.id, saveOverride, deleteOverride, saveLight, deleteLight, previewLight,
        clearPreview, saveLevelLighting, resetLevelLighting, previewLevelLighting, clearLevelLightingPreview]);
    if (load.error) throw new Error("Lichter konnten nicht geladen werden.");
    return <EncounterLightContext.Provider value={value}>{children}</EncounterLightContext.Provider>;
}
